import { useState, useEffect } from "react";
import SEOHead from "@/components/SEOHead";
import { supabase } from "@/integrations/supabase/client";
import { ProductCard } from "@/components/loja/ProductCard";
import { CategoryFilter } from "@/components/loja/CategoryFilter";
import { Skeleton } from "@/components/ui/skeleton";
import { ShoppingBag } from "lucide-react";
import PageInstructions from "@/components/PageInstructions";
import { isAndroidTWA } from "@/utils/runtimeContext";
import AndroidNeutralNotice from "@/components/legal/AndroidNeutralNotice";

export interface Product {
  id: string;
  title: string;
  description: string;
  image_url: string;
  affiliate_link: string;
  category: string;
  price_text?: string;
  display_order: number;
}

const LojaPage = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [isLoading, setIsLoading] = useState(true);
  const inTWA = isAndroidTWA();

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (selectedCategory === "all") {
      setFilteredProducts(products);
    } else {
      setFilteredProducts(products.filter(p => p.category === selectedCategory));
    }
  }, [selectedCategory, products]);

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from("affiliate_products")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true })
        .order("created_at", { ascending: false });

      if (error) throw error;
      setProducts(data || []);
      setFilteredProducts(data || []);
    } catch (error) {
      console.error("Erro ao carregar produtos:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const categories = Array.from(new Set(products.map(p => p.category)));

  if (inTWA) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <AndroidNeutralNotice />
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Loja - Planilhas e Métodos para Jogo do Bicho e Loterias"
        description="Produtos selecionados para o Jogo do Bicho e Loterias: planilhas poderosas, cursos, ebooks, ferramentas e software. Potencialize seus jogos com ferramentas exclusivas."
        keywords="planilhas jogo do bicho, métodos bicho, software mega-sena, planilhas loterias, ferramentas bicho, cursos jogo do bicho, ebooks loterias"
        canonical="/loja"
      />
    <div className="min-h-screen bg-gradient-to-b from-background to-accent/5">
      <div className="container mx-auto px-4 py-8 space-y-8">
        {/* Hero Section */}
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="p-4 rounded-full bg-primary/10 backdrop-blur-sm">
              <ShoppingBag className="h-12 w-12 text-primary" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            Loja & Produtos
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Descubra produtos selecionados e ferramentas exclusivas para potencializar seus jogos
          </p>
        </div>

        <PageInstructions
          title="📖 Como usar a Loja"
          steps={[
            "Navegue pelas categorias usando os filtros acima dos produtos",
            "Clique no produto que te interessa para ver os detalhes",
            "Ao clicar em 'Comprar' ou 'Ver mais', você será redirecionado para a loja do produto em uma nova aba",
            "Complete a compra diretamente no site do vendedor"
          ]}
        />

        {/* Category Filter */}
        {categories.length > 0 && (
          <CategoryFilter
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
            productCounts={products.reduce((acc, p) => {
              acc[p.category] = (acc[p.category] || 0) + 1;
              return acc;
            }, {} as Record<string, number>)}
          />
        )}

        {/* Products Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="space-y-4">
                <Skeleton className="aspect-video w-full rounded-xl" />
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16 space-y-4">
            <div className="p-6 rounded-full bg-muted/50 inline-flex">
              <ShoppingBag className="h-16 w-16 text-muted-foreground" />
            </div>
            <h3 className="text-2xl font-semibold">Nenhum produto disponível</h3>
            <p className="text-muted-foreground">
              {selectedCategory === "all" 
                ? "Em breve novos produtos serão adicionados."
                : "Nenhum produto encontrado nesta categoria."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default LojaPage;
